import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { LoadingButton } from '@mui/lab';
import {
	Checkbox,
	FormControlLabel,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	TextField,
	Tooltip,
} from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { gql, useLazyQuery, useMutation, useQuery } from '@parkchirp/api-client';
import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import { CREATE_TAX_EXEMPT_CERTIFICATE, UPDATE_TAX_EXEMPT_CERTIFICATE } from '../../mutations';
import { GET_PUT_SIGNED_URL, LIST_TAX_TABLE_TAXES_V2, SEARCH_BY_ACCOUNT_ID } from '../../queries';
import { Dayjs, dayjs, uploadDocToS3 } from '../../utils';
import { FileSelection } from '../fileUpload/fileUpload';
import { StateSelect } from '../inputs/stateSelect';

const LIST_INVOICES_FOR_SUBSCRIPTION = gql`
  query listInvoices(
	$limit: Int!
	$offset: Int!
	$filters: [Filter]
  ) {
	listInvoices(
	  limit: $limit
	  offset: $offset
	  filters: $filters
	) {
	  count
	}
  }
`;

type Attachment = {
	file: any;
	filePath: string;
};

interface Props {
	setShowSnackbar?: (showSnackbar: Partial<any>) => void;
	singleSubscriptionData: any;

	setShowDialog: Dispatch<SetStateAction<ReactNode | null>>;
	refetch?: () => void;
}

type TaxTableTax = {
	id: string;
	name: string;
	taxNumber: number;
};

type FormState = {
	taxExemptState: string | null;
	issueDate: Dayjs | null;
	expirationDate: Dayjs | null;
	companyName: string | null;
	organizationType: string | null;
	exemptionNumber: string | null;
	attachment: Attachment | null;
};

type AdditionalFormState = {
	exemptTax1: string | null;
	exemptTax2: string | null;
	exemptTax3: string | null;
	exemptTax4: string | null;
	parkingPeriodStart: Dayjs | null;
	invoiceCreationStartDate: Dayjs | null;
	adjustmentDateType: string;
};

const REQUIRED: Record<keyof FormState, string> = {
	companyName: 'Company Name',
	issueDate: 'Issue Date',
	expirationDate: 'Expiration Date',
	exemptionNumber: 'Exemption Number',
	taxExemptState: 'Issue State',
	organizationType: 'Organization Type',
	attachment: 'Attachment',
};

const ADDITIONAL_FIELD_LABELS: Record<keyof AdditionalFormState, string> = {
	exemptTax1: 'Exempt Taxes 1',
	exemptTax2: 'Exempt Taxes 2',
	exemptTax3: 'Exempt Taxes 3',
	exemptTax4: 'Exempt Taxes 4',
	invoiceCreationStartDate: 'Invoice Creation Date Start',
	parkingPeriodStart: 'Parking Period Start',
	adjustmentDateType: 'Adjustment Date Type',
};

const FIELDS: Array<{
	key: keyof FormState;
	label: string;
	type?: 'text' | 'date' | 'number' | 'file' | 'select' | 'state';
	required?: boolean;
	options?: Array<{ label: string; value: string }>;
	placeholder?: string;
}> = [
	{ key: 'companyName', label: REQUIRED.companyName, required: true, placeholder: 'Acme Corp' },
	{ key: 'exemptionNumber', label: REQUIRED.exemptionNumber, required: true, placeholder: 'EX-123456' },
	{ key: 'organizationType', label: REQUIRED.organizationType, required: true, placeholder: 'Organization Type' },
	{ key: 'taxExemptState', label: REQUIRED.taxExemptState, type: 'state', required: true, placeholder: 'Illinois' },
	{ key: 'issueDate', label: REQUIRED.issueDate, type: 'date', required: true },
	{ key: 'expirationDate', label: REQUIRED.expirationDate, type: 'date', required: true },
];

const ADDITIONAL_FIELDS: Array<{
	key: keyof AdditionalFormState;
	label: string;
	type?: 'text' | 'date' | 'number' | 'file' | 'select' | 'state';
	required?: boolean;
	options?: Array<{ label: string; value: string }>;
	placeholder?: string;
}> = [
	{
		key: 'exemptTax1',
		label: ADDITIONAL_FIELD_LABELS.exemptTax1,
		required: false,
		type: 'select',
	},
	{
		key: 'exemptTax2',
		label: ADDITIONAL_FIELD_LABELS.exemptTax2,
		required: false,
		type: 'select',
	},
	{
		key: 'exemptTax3',
		label: ADDITIONAL_FIELD_LABELS.exemptTax3,
		required: false,
		type: 'select',
	},
	{
		key: 'exemptTax4',
		label: ADDITIONAL_FIELD_LABELS.exemptTax4,
		required: false,
		type: 'select',
	},
	{
		key: 'adjustmentDateType',
		label: ADDITIONAL_FIELD_LABELS.adjustmentDateType,
		type: 'select',
		options: [
			{ label: 'Invoice Date', value: 'Invoice' },
			{ label: 'Parking Period', value: 'Parking' },
		],
		required: true,
	},
	{
		key: 'invoiceCreationStartDate',
		label: ADDITIONAL_FIELD_LABELS.invoiceCreationStartDate,
		type: 'date',
		required: true,
		placeholder: '',
	},
	{
		key: 'parkingPeriodStart',
		label: ADDITIONAL_FIELD_LABELS.parkingPeriodStart,
		type: 'date',
		required: true,
		placeholder: '',
	},
];

const BUCKET = `parkchirp-files`;

export const TaxExemptCertificateUploadModal = ({
	setShowSnackbar,
	singleSubscriptionData,
	setShowDialog,
	refetch,
}: Props) => {
	const [accountNumber, setAccountNumber] = useState('');
	const [loading, setLoading] = useState(false);
	const [singleSubscriptionDataState, setSingleSubscriptionDataState] = useState(singleSubscriptionData);
	const [searchByAccountId] = useLazyQuery(SEARCH_BY_ACCOUNT_ID, {
		context: {
			apiVersion: 'v2',
		},
		fetchPolicy: 'network-only',
	});

	const { data: invoiceData } = useQuery(LIST_INVOICES_FOR_SUBSCRIPTION, {
		variables: {
			limit: 1,
			offset: 0,
			filters: [
				{
					propertyName: 'accountId',
					value: singleSubscriptionData?.accountId.slice(3),
					op: 'equals',
				},
			],
		},
		context: { apiVersion: 'v2' },
		fetchPolicy: 'network-only',
		skip: !singleSubscriptionData?.id,
	});
	const invoiceCount = invoiceData?.listInvoices?.count ?? 0;

	const hasExistingCert = !!singleSubscriptionData?.id;
	const isLocked = hasExistingCert && invoiceCount > 0;

	const [form, setForm] = useState<FormState>({
		taxExemptState: singleSubscriptionData?.issueState || '',
		issueDate: singleSubscriptionData?.issueDate || '',
		expirationDate: singleSubscriptionData?.expirationDate || '',
		companyName: singleSubscriptionData?.companyName || '',
		organizationType: singleSubscriptionData?.organizationType || '',
		exemptionNumber: singleSubscriptionData?.exemptionNumber,
		attachment: singleSubscriptionData?.filePath
			? {
					file: {
						name: singleSubscriptionData?.filePath?.split('_')[1],
						type: singleSubscriptionData?.filePath?.split('_')[1].split('.')[1],
					},
					filePath: singleSubscriptionData?.filePath,
				}
			: null,
	});
	// we are using this to determine if it already has additional configuration saved
	const taxExemptStart = singleSubscriptionData?.subscriptionTaxExemptTaxes?.[0]?.effectiveStartDate;

	const isValidStart = taxExemptStart ? dayjs(taxExemptStart).isValid() : false;
	const isFirstOfMonth = taxExemptStart ? dayjs.utc(taxExemptStart, 'x').date() === 1 : false;

	const parkingPeriodStart = isValidStart && isFirstOfMonth ? dayjs.utc(taxExemptStart, 'x') : null;

	const invoiceCreationStartDate = isValidStart && !isFirstOfMonth ? dayjs.utc(taxExemptStart, 'x') : null;

	const additionalDatesDisabled =
		invoiceCount > 0 &&
		singleSubscriptionData?.subscriptionTaxExemptTaxes?.[0] &&
		((parkingPeriodStart && parkingPeriodStart.isSameOrBefore(dayjs.utc(), 'day')) ||
			(invoiceCreationStartDate && invoiceCreationStartDate.isSameOrBefore(dayjs.utc(), 'day')));

	const [additionalForm, setAdditionalForm] = useState<AdditionalFormState>({
		exemptTax1: null,
		exemptTax2: null,
		exemptTax3: null,
		exemptTax4: null,
		parkingPeriodStart,
		invoiceCreationStartDate,
		adjustmentDateType:
			singleSubscriptionData?.subscriptionTaxExemptTaxes?.[0]?.adjustmentDateType === 'Creation_Date'
				? 'Invoice'
				: 'Parking',
	});

	const [showAdditionalFormData, setShowAdditionalFormData] = useState<boolean>(taxExemptStart);
	const [taxOptions, setTaxOptions] = useState<any[]>([]);
	const [saving, setSaving] = useState<boolean>(false);
	const [updateTaxExemptCert] = useMutation(UPDATE_TAX_EXEMPT_CERTIFICATE, {
		context: {
			apiVersion: 'v2',
		},
	});
	const [createTaxExemptCert] = useMutation(CREATE_TAX_EXEMPT_CERTIFICATE, {
		context: {
			apiVersion: 'v2',
		},
	});
	const [getSignedUrl] = useLazyQuery(GET_PUT_SIGNED_URL);

	useQuery(LIST_TAX_TABLE_TAXES_V2, {
		variables: {
			facilityId: singleSubscriptionDataState?.facilityId,
		},
		fetchPolicy: 'network-only',
		context: {
			apiVersion: 'v2',
		},
		skip: !singleSubscriptionDataState?.facilityId,
		onCompleted: (data) => {
			const taxTableTaxes = data?.listTaxTableTaxes;
			if (taxTableTaxes) {
				const taxTableOptions = [...taxTableTaxes]
					.sort((a: TaxTableTax, b: TaxTableTax) => a.taxNumber - b.taxNumber)
					.map((tax: TaxTableTax) => ({
						label: `${tax.name} (Tax #${tax.taxNumber})`,
						value: tax.id,
						id: tax.id,
						taxNumber: tax.taxNumber,
					}));
				const currentAdditionalForm = { ...additionalForm };
				if (singleSubscriptionData?.subscriptionTaxExemptTaxes?.some((t) => t.taxNumber === 1)) {
					currentAdditionalForm.exemptTax1 = taxTableOptions.find((opt) => opt.taxNumber === 1)?.id || null;
				}
				if (singleSubscriptionData?.subscriptionTaxExemptTaxes?.some((t) => t.taxNumber === 2)) {
					currentAdditionalForm.exemptTax2 = taxTableOptions.find((opt) => opt.taxNumber === 2)?.id || null;
				}
				if (singleSubscriptionData?.subscriptionTaxExemptTaxes?.some((t) => t.taxNumber === 3)) {
					currentAdditionalForm.exemptTax3 = taxTableOptions.find((opt) => opt.taxNumber === 3)?.id || null;
				}
				if (singleSubscriptionData?.subscriptionTaxExemptTaxes?.some((t) => t.taxNumber === 4)) {
					currentAdditionalForm.exemptTax4 = taxTableOptions.find((opt) => opt.taxNumber === 4)?.id || null;
				}
				setAdditionalForm({ ...currentAdditionalForm });
				setTaxOptions([...taxTableOptions]);
			}
		},
	});

	const onInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: keyof FormState) => {
		const value: string | number | null = e.target.value ?? null;
		setForm((prev) => ({ ...prev, [key]: value }));
	};

	const onInputChangeAdditional = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		key: keyof AdditionalFormState,
	) => {
		const value: string | number | null = e.target.value ?? null;

		setAdditionalForm((prev) => ({ ...prev, [key]: value }));
	};

	const onDateChange = (key: 'issueDate' | 'expirationDate') => (val: Dayjs | null) => {
		setForm((prev) => ({ ...prev, [key]: val }));
	};

	const onDateChangeAdditional =
		(key: 'invoiceCreationStartDate' | 'invoiceCreationEndDate' | 'parkingPeriodStart' | 'parkingPeriodEnd') =>
		(val: Dayjs | null) => {
			if (val.isValid()) {
				setAdditionalForm((prev) => ({ ...prev, [key]: val }));
			} else {
				setAdditionalForm((prev) => ({ ...prev, [key]: null }));
			}
		};

	const onStateChange = (val: string | null) => {
		setForm((prev) => ({ ...prev, taxExemptState: val }));
	};

	const setAttachment = (file: Attachment | null) => {
		setForm((prev) => ({ ...prev, attachment: file }));
	};

	const handleFieldChange = (key, val: string) => {
		setAdditionalForm((prev) => ({
			...prev,
			invoiceCreationStartDate: null,
			invoiceCreationEndDate: null,
			parkingPeriodStart: null,
			parkingPeriodEnd: null,
			[key]: val,
		}));
	};

	const validate = () => {
		for (const field of FIELDS) {
			if (field.required) {
				const v = form[field.key];
				const empty = v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

				if (empty) {
					setShowSnackbar({
						severity: 'error',
						message: `${REQUIRED[field.key]} is a required field`,
					});
					return false;
				}
			}
		}
		if (showAdditionalFormData) {
			if (
				!additionalForm.exemptTax1 &&
				!additionalForm.exemptTax2 &&
				!additionalForm.exemptTax3 &&
				!additionalForm.exemptTax4
			) {
				setShowSnackbar({
					severity: 'error',
					message: `At least 1 tax is required to submit`,
				});
				return false;
			}
			if (!additionalForm.parkingPeriodStart && !additionalForm.invoiceCreationStartDate) {
				setShowSnackbar({
					severity: 'error',
					message: `Please select either a parking period or an invoice creation date`,
				});
				return false;
			}
			if (
				additionalForm.adjustmentDateType === 'Invoice' &&
				dayjs(additionalForm.invoiceCreationStartDate).isBefore(dayjs(), 'day')
			) {
				setShowSnackbar({
					severity: 'error',
					message: `Invoice Date must be in the future`,
				});
				return false;
			}
		}
		return true;
	};

	const isSaveDisabled = useMemo(
		() =>
			FIELDS.some((f) => {
				if (!f.required) return false;
				const v = form[f.key];
				return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
			}),
		[form],
	);

	const handleUploadCert = async () => {
		if (!validate()) return;

		setSaving(true);

		const {
			companyName,
			issueDate,
			taxExemptState: issueState,
			exemptionNumber,
			organizationType,
			expirationDate,
			attachment,
		} = form;

		const {
			exemptTax1,
			exemptTax2,
			exemptTax3,
			exemptTax4,
			parkingPeriodStart: parkingPeriodStartVal,
			invoiceCreationStartDate: invoiceCreationStartDateVal,
			adjustmentDateType,
		} = additionalForm;

		const { file } = attachment;
		const { name: fileName } = file;

		const baseVars = {
			companyName,
			issueDate: dayjs.utc(issueDate).format('YYYY-MM-DD'),
			issueState,
			exemptionNumber,
			organizationType,
			expirationDate: dayjs.utc(expirationDate).format('YYYY-MM-DD'),
			exemptTax1,
			exemptTax2,
			exemptTax3,
			exemptTax4,
			parkingPeriodStart: parkingPeriodStartVal,
			invoiceCreationStartDate: invoiceCreationStartDateVal,
			adjustmentDateType,
			subscriptionId: singleSubscriptionDataState.subscriptionId,
			s3Key: singleSubscriptionData?.filePath || null,
		};

		const context = {
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			apiVersion: 'v2',
		};

		try {
			if (file instanceof File) {
				const s3Key = `${singleSubscriptionDataState.subscriptionId}/${v4()}_${fileName}`;

				const { data } = await getSignedUrl({
					variables: {
						bucket: BUCKET,
						s3Key,
					},
					context: {
						apiVersion: 'v2',
					},
				});
				const url = data?.getPutSignedUrl?.url;

				if (!url) {
					throw new Error('Failed to get signed url');
				}

				const uploadResponse = await uploadDocToS3(url, file);

				if (!uploadResponse) {
					throw new Error('Failed to upload document');
				}

				baseVars.s3Key = s3Key;
			}

			const variables = {
				...baseVars,
				...(hasExistingCert && { certId: singleSubscriptionData.id }),
			};
			const mutation = hasExistingCert ? updateTaxExemptCert : createTaxExemptCert;

			await mutation({ variables, context });

			setShowSnackbar({
				message: `Successfully ${hasExistingCert ? 'updated' : 'created'} your certificate`,
				severity: 'success',
			});

			setShowDialog(null);
			if (refetch) {
				refetch();
			}
		} catch {
			setShowSnackbar({
				message: 'There was an error creating your certificate',
				severity: 'error',
			});
		} finally {
			setSaving(false);
		}
	};

	const handleCheckAdditionalFormData = (e) => {
		const { checked } = e.target;
		if (!checked) {
			setAdditionalForm({
				exemptTax1: null,
				exemptTax2: null,
				exemptTax3: null,
				exemptTax4: null,
				parkingPeriodStart,
				invoiceCreationStartDate,
				adjustmentDateType: 'Invoice',
			});
		}
		setShowAdditionalFormData(checked);
	};

	const isSaveDisabledForAdditionalConfig = () => {
		if (showAdditionalFormData) {
			if (
				!additionalForm.exemptTax1 &&
				!additionalForm.exemptTax2 &&
				!additionalForm.exemptTax3 &&
				!additionalForm.exemptTax4
			) {
				return true;
			}
			if (!additionalForm.parkingPeriodStart && !additionalForm.invoiceCreationStartDate) {
				return true;
			}
		}
		return false;
	};

	const handleAccountSelect = async () => {
		setLoading(true);
		try {
			const { data, error } = await searchByAccountId({
				variables: {
					accountId: Number(accountNumber),
				},
			});
			if (error) {
				throw error;
			}
			setSingleSubscriptionDataState({
				accountId: data?.getSubscriptionByAccountId?.accountId,
				subscriptionId: data?.getSubscriptionByAccountId?.id,
				facilityName: data?.getSubscriptionByAccountId?.billingProfile?.facility?.facilityName,
				facilityNumber: data?.getSubscriptionByAccountId?.billingProfile?.facility?.facilityNumber,
				subscriptionName: data?.getSubscriptionByAccountId?.subscriptionName,
				facilityId: data?.getSubscriptionByAccountId?.billingProfile?.facility?.id,
			});
		} catch (err) {
			console.error(err);
			setShowSnackbar({
				severity: 'error',
				message: 'Please enter a valid account ID.',
			});
		} finally {
			setLoading(false);
		}
	};

	if (!singleSubscriptionDataState) {
		return (
			<Grid container flexDirection='column' padding='20px' gap={2}>
				<IconButton
					onClick={() => setShowDialog(null)}
					size='small'
					style={{ position: 'absolute', top: 8, right: 8 }}
				>
					✕
				</IconButton>

				<h1>Select an Account</h1>

				<TextField
					label='Account ID'
					value={accountNumber}
					onChange={(e) => {
						const val = e.target.value.replace(/\D/g, '');
						setAccountNumber(val);
					}}
					InputProps={{
						startAdornment: <InputAdornment position='start'>AC-</InputAdornment>,
					}}
					fullWidth
				/>

				<Button variant='contained' onClick={handleAccountSelect} disabled={!accountNumber || loading}>
					{loading ? 'Loading...' : 'Continue'}
				</Button>
			</Grid>
		);
	}

	return (
		<Grid
			container
			flexDirection='column'
			minWidth='1000px'
			padding='20px'
			data-cy='tax-exempt-certificate-modal-container'
		>
			<IconButton
				onClick={() => setShowDialog(null)}
				size='small'
				style={{ position: 'absolute', top: 8, right: 8 }}
				data-cy='tax-exempt-certificate-modal-close-button'
			>
				✕
			</IconButton>
			<Grid paddingBottom='20px' container flexDirection='column'>
				<Grid item xs={12}>
					<h1>Tax Exempt Certificate Configuration</h1>
				</Grid>
				<Grid item xs={12}>
					<p data-cy='account-id-text'>Account ID: {singleSubscriptionDataState?.accountId}</p>
				</Grid>
				<Grid item xs={12}>
					<p data-cy='subscription-text'>Subscription: {singleSubscriptionDataState?.subscriptionName}</p>
				</Grid>
				<Grid item xs={12}>
					<p data-cy='facility-text'>
						Facility: {singleSubscriptionDataState?.facilityName} (
						{singleSubscriptionDataState?.facilityNumber})
					</p>
				</Grid>
			</Grid>
			<Grid paddingBottom='16px' container spacing={2}>
				{FIELDS.map((f) => {
					const key = String(f.key);

					const value =
						f.type === 'number'
							? ((form[key] as number | null) ?? '')
							: ((form[key] as string | null) ?? '');

					if (f.type === 'state') {
						return (
							<Grid item xs={6}>
								<InputLabel>{f.label}*</InputLabel>
								<StateSelect label='' value={value} onChange={(e) => onStateChange(e.target.value)} />
							</Grid>
						);
					}

					if (f.type === 'date') {
						return (
							<Grid item xs={6}>
								<InputLabel>{f.label}*</InputLabel>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DatePicker
										format='MM/DD/YYYY'
										value={dayjs(value).isValid() ? dayjs.utc(value) : null}
										onChange={onDateChange(f.key as 'issueDate' | 'expirationDate')}
										slotProps={{
											textField: {
												fullWidth: true,
												required: true,
											},
										}}
									/>
								</LocalizationProvider>
							</Grid>
						);
					}

					return (
						<Grid item xs={6}>
							<InputLabel>{f.label}*</InputLabel>
							<TextField
								key={key}
								id={key}
								name={key}
								aria-label={f.label}
								fullWidth
								required={!!f.required}
								type={f.type ?? 'text'}
								placeholder={f.placeholder}
								value={value}
								onChange={(e) => onInputChange(e, f.key)}
							/>
						</Grid>
					);
				})}
			</Grid>

			<FormControlLabel
				control={
					<Checkbox
						checked={showAdditionalFormData}
						onChange={handleCheckAdditionalFormData}
						disabled={taxExemptStart}
					/>
				}
				label='Apply this certificate to specific taxes (This will impact the taxes that are charged on invoices and will clear previous manual tax exemptions)'
				sx={{ mb: 2 }}
				disabled={isLocked}
			/>

			{showAdditionalFormData && (
				<Grid paddingBottom='16px' container spacing={2}>
					{ADDITIONAL_FIELDS.map((f) => {
						const key = String(f.key);
						const options = ['exemptTax1', 'exemptTax2', 'exemptTax3', 'exemptTax4'].includes(f.key)
							? taxOptions
							: f.options;

						const value =
							f.type === 'number'
								? ((additionalForm[key] as number | null) ?? '')
								: ((additionalForm[key] as string | null) ?? '');

						if (f.type === 'select' && f.key === 'adjustmentDateType') {
							return (
								<Grid item xs={2}>
									<InputLabel>{f.label}*</InputLabel>
									<TextField
										key={key}
										id={key}
										name={key}
										aria-label={f.label}
										select
										fullWidth
										required={f.required}
										disabled={additionalDatesDisabled || isLocked}
										value={(additionalForm[key] as string) ?? ''}
										onChange={(e) => {
											handleFieldChange(f.key, e.target.value);
										}}
									>
										{options?.map((opt) => (
											<MenuItem key={opt.value} value={opt.value}>
												{opt.label}
											</MenuItem>
										))}
									</TextField>
								</Grid>
							);
						}

						if (
							f.type === 'select' &&
							['exemptTax1', 'exemptTax2', 'exemptTax3', 'exemptTax4'].includes(key)
						) {
							return (
								<Grid item xs={3}>
									<InputLabel>{f.label}</InputLabel>
									<TextField
										key={key}
										id={key}
										name={key}
										aria-label={f.label}
										select
										fullWidth
										required={f.required}
										disabled={additionalDatesDisabled || isLocked}
										value={additionalForm[key] || ''}
										onChange={(e) =>
											setAdditionalForm((prev) => ({
												...prev,
												[key]: e.target.value,
											}))
										}
										InputProps={{
											endAdornment: additionalForm[key] && !taxExemptStart && (
												<InputAdornment position='end'>
													<IconButton
														size='small'
														onClick={() =>
															setAdditionalForm((prev) => ({
																...prev,
																[key]: '',
															}))
														}
													>
														<CloseIcon fontSize='small' />
													</IconButton>
												</InputAdornment>
											),
										}}
									>
										{options
											?.filter((o) => o.taxNumber === Number(key.match(/\d+$/)[0]))
											.map((opt) => (
												<MenuItem key={opt.value} value={opt.value}>
													{opt.label}
												</MenuItem>
											))}
									</TextField>
								</Grid>
							);
						}

						if (f.type === 'date' && key.includes(additionalForm.adjustmentDateType.toLowerCase())) {
							// Use same tooltip logic
							const type = additionalForm?.adjustmentDateType;
							let tooltipText = 'Choose an appropriate option based on your configuration.';
							if (type === 'Invoice') {
								tooltipText =
									'Any invoice created on or after this date will be exempt from the configured taxes. This includes any invoices created for parking periods prior to the invoice creation date. The tax exemption will expire on the Expiration Date, which is configured above. The Expiration Date is based on the Parking Period, not the date invoices are created.';
							} else if (type === 'Parking') {
								tooltipText =
									'Any invoice created on or after the selected parking period will be exempt from the configured taxes. If you select a parking period in arrears, the invoices from those parking periods will be adjusted. The tax exemption will expire on the Expiration Date, which is configured above. The Expiration Date is based on the Parking Period, not the date invoices are created.';
							}
							return (
								<Grid item xs={5}>
									<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
										<InputLabel>
											{f.label}
											{f.required ? '*' : ''}
										</InputLabel>
										<Tooltip title={tooltipText} arrow placement='top'>
											<InfoIcon fontSize='small' />
										</Tooltip>
									</div>
									<LocalizationProvider dateAdapter={AdapterDayjs}>
										<DatePicker
											format='MM/DD/YYYY'
											value={dayjs(value).isValid() ? dayjs.utc(value) : null}
											onChange={onDateChangeAdditional(
												f.key as 'parkingPeriodStart' | 'invoiceCreationStartDate',
											)}
											shouldDisableDate={(date) => {
												const d = dayjs(date);
												const today = dayjs().startOf('day');
												if (f.key === 'parkingPeriodStart') {
													return d.date() !== 1;
												}
												return d.isBefore(today, 'day');
											}}
											disabled={additionalDatesDisabled || isLocked}
											slotProps={{
												textField: {
													fullWidth: true,
													required: true,
												},
											}}
										/>
									</LocalizationProvider>
								</Grid>
							);
						}

						if (f.type === 'date') return null;

						return (
							<Grid item xs={6} key={key}>
								<InputLabel>{f.label}*</InputLabel>
								<TextField
									id={key}
									name={key}
									aria-label={f.label}
									fullWidth
									required={!!f.required}
									type={f.type ?? 'text'}
									placeholder={f.placeholder}
									value={value}
									disabled={isLocked}
									onChange={(e) => onInputChangeAdditional(e, f.key)}
								/>
							</Grid>
						);
					})}
				</Grid>
			)}
			<Grid item xs={12} style={{ paddingBottom: '16px' }}>
				<FileSelection
					attachment={form.attachment}
					setAttachment={setAttachment}
					setShowSnackbar={setShowSnackbar}
					required
				/>
			</Grid>
			<Grid spacing={2} container>
				<Grid item xs={6}>
					<Button fullWidth onClick={() => setShowDialog(null)} data-cy='modal-cancel-button'>
						Cancel
					</Button>
				</Grid>
				<Grid item xs={6}>
					<LoadingButton
						fullWidth
						onClick={handleUploadCert}
						disabled={isSaveDisabled || !form.attachment || isSaveDisabledForAdditionalConfig()}
						data-cy='modal-confirm-button'
						loading={saving}
					>
						Save
					</LoadingButton>
				</Grid>
			</Grid>
		</Grid>
	);
};
